import React from 'react';
import {
    Box,
    Column,
    FlexBox,
    ResponsiveImage,
    Spinner,
    Typography,
    tokensRaw,
    useScreenClass,
} from '@vp/swan';
import styled from 'styled-components';
import { PreviewModal, PreviewModalVariant } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { Image } from './StartingProjectDisplay';

const StyledResponsiveImage = styled(ResponsiveImage)`
    object-fit: cover;
    cursor: zoom-in;
    width: 100%;
`;

const StyledBox = styled(Box)`
    width: 100%;
    height: 100%;
    display: inline-block;
`;

const StyledColumn = styled(Column)`
    display: inline-block;
`;

type StartingProjectImagesProps = {
    images: Image[] | undefined;
    onDownload?: (image: Image) => Promise<void> | void;
};

export const StartingProjectImages = ({ images }: StartingProjectImagesProps) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [currentSlide, setCurrentSlide] = React.useState(0);
    const screenClass = useScreenClass();

    if (!images) {
        return <Spinner accessibleText={__('Loading…')} />;
    }

    return (
        <>
            <PreviewModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                currentSlide={currentSlide}
                setCurrentSlide={setCurrentSlide}
                previewFiles={images}
                variant={PreviewModalVariant.WORK_ENTITY}
            />
            {images.map((image, i) => (
                <FlexBox
                    flexDirection="column"
                    alignItems="center"
                    mr="5"
                    {...(screenClass === 'xs' ? { mt: 4 } : {})}
                >
                    <FlexBox
                        style={{
                            height: '80px',
                            width: '80px',
                            borderRadius: tokensRaw.SwanSemBorderRadiusStandard,
                            backgroundColor: 'white',
                            overflow: 'hidden',
                        }}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <StyledResponsiveImage
                            src={image.url}
                            alt={
                                image.title ||
                                __('Starting project image', {
                                    _context:
                                        'alt text for a starting project image that is used as a reference for designers',
                                })
                            }
                            style={{
                                maxHeight: '100%',
                                maxWidth: '100%',
                                objectFit: 'contain',
                            }}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                                setCurrentSlide(i);
                                setIsOpen(!isOpen);
                            }}
                        />
                    </FlexBox>
                    <Typography
                        fontSize="xsmall"
                        textAlign="center"
                        mt="2"
                        style={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            maxWidth: '80px',
                        }}
                    >
                        {image.title}
                    </Typography>
                </FlexBox>
            ))}
        </>
    );
};
export const LogomakerStartingProjectImages = ({
    images,
    onDownload,
}: StartingProjectImagesProps) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [currentSlide, setCurrentSlide] = React.useState(0);
    const [imageLoaded, setImageLoaded] = React.useState(false);

    if (!images) {
        return <Spinner accessibleText={__('Loading…')} />;
    }
    return (
        <>
            <PreviewModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                currentSlide={currentSlide}
                setCurrentSlide={setCurrentSlide}
                previewFiles={images}
                variant={PreviewModalVariant.LOGOMAKER}
                onDownload={onDownload}
                showTitle={false}
            />
            {images.map((image, i) => (
                <FlexBox
                    key={`logomaker-starting-project-image-${i}`}
                    flexDirection="column"
                    style={{ maxWidth: '172px' }}
                >
                    <Typography fontSize="standard" fontWeight="normal">
                        {image.title}
                    </Typography>
                    <StyledBox mt={4} style={{ position: 'relative', aspectRatio: '1' }}>
                        {!imageLoaded && (
                            <FlexBox
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                }}
                                justifyContent="center"
                                alignItems="center"
                            >
                                <Spinner accessibleText={__('Loading…')} />
                            </FlexBox>
                        )}
                        <StyledResponsiveImage
                            src={image.url}
                            alt={image.title}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                                setCurrentSlide(i);
                                setIsOpen(!isOpen);
                            }}
                            onLoad={() => setImageLoaded(true)}
                            style={{ visibility: imageLoaded ? 'visible' : 'hidden' }}
                        />
                    </StyledBox>
                </FlexBox>
            ))}
        </>
    );
};
export const CollaborationStartingProjectImages = ({ images }: StartingProjectImagesProps) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [currentSlide, setCurrentSlide] = React.useState(0);

    if (!images) {
        return <Spinner accessibleText={__('Loading…')} />;
    }

    return (
        <>
            <PreviewModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                currentSlide={currentSlide}
                setCurrentSlide={setCurrentSlide}
                previewFiles={images}
                variant={PreviewModalVariant.WORK_ENTITY}
            />
            {images.map((image, i) => (
                <StyledColumn mt={4} mr={5} span={5}>
                    <FlexBox flexDirection="column" alignItems="center">
                        <StyledBox py={'3'} align="center">
                            <StyledResponsiveImage
                                src={image.url}
                                alt={image.title}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    setCurrentSlide(i);
                                    setIsOpen(!isOpen);
                                }}
                            />
                        </StyledBox>
                        <Typography fontSize="small" fontWeight="bold" textAlign="center">
                            {image.title}
                        </Typography>
                    </FlexBox>
                </StyledColumn>
            ))}
        </>
    );
};
